import { Scissors } from '@app/components/icons/scissors';
import { TrashNewIcon } from '@app/components/icons/trash-new';
import { Undo } from '@app/components/icons/undo';
import { useTranslation } from '@hopin-team/provider-translation';
import cx from 'classnames';
import React, { useState } from 'react';

import type { ComputedJoint } from '../utils/cuts-to-segments';
import { formatTime } from '../utils/time-calculations';

type TimelineJointProps = {
  joint: ComputedJoint;
  isSelected?: boolean;
  onClick?: (jointId: string) => void;
  onRemoveCut?: (jointId: string) => void;
};

export const TimelineJoint: React.FC<TimelineJointProps> = ({
  joint,
  isSelected = false,
  onClick,
  onRemoveCut,
}) => {
  const { t } = useTranslation();
  const hasDeletedDuration = joint.deletedDuration > 0;
  const [isHovered, setIsHovered] = useState(false);
  const deletedDurationLabel = formatTime(joint.deletedDuration / 1000);

  return (
    <div className="relative">
      <button
        type="button"
        className={cx(
          'absolute left-0 -top-16 flex h-6 w-6 -translate-x-1/2 rotate-45 items-center justify-center rounded-[6px] text-white shadow-[0_2px_8px_rgba(0,0,0,0.35)] outline-none',
          hasDeletedDuration ? 'bg-red-400' : 'bg-[#73CB87]',
          isSelected && 'ring-2 ring-white/70',
        )}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        aria-label={`timeline-joint-${joint.id}`}
        data-testid={`timeline-joint-${joint.id}`}
        onClick={() => onClick?.(joint.id)}
      >
        <Scissors
          className="h-4 w-4 -rotate-45"
          data-testid={
            hasDeletedDuration
              ? `timeline-joint-marker-deleted-${joint.id}`
              : `timeline-joint-marker-cut-${joint.id}`
          }
        />
      </button>
      {hasDeletedDuration && isHovered && !isSelected && (
        <div
          className="pointer-events-none absolute -top-10 left-0 -translate-x-1/2 z-20 whitespace-nowrap rounded bg-black/85 px-1.5 py-0.5 text-[10px] text-white"
          data-testid={`timeline-joint-deleted-tooltip-${joint.id}`}
        >
          {t('content-recording.editor-v2.deleted-duration-tooltip', {
            duration: deletedDurationLabel,
          })}
        </div>
      )}
      {isSelected && (
        <button
          type="button"
          className="absolute -top-10 left-0 z-20 -translate-x-1/2 flex items-center gap-1.5 whitespace-nowrap rounded bg-spring-neutral-b5 px-2 py-1 text-[10px] text-white ring-1 ring-spring-neutral-b0/25 hover:bg-spring-neutral-b4"
          onClick={event => {
            event.stopPropagation();
            onRemoveCut?.(joint.id);
          }}
          data-testid={`timeline-joint-action-${joint.id}`}
        >
          {hasDeletedDuration ? (
            <Undo className="w-3 h-3 shrink-0" />
          ) : (
            <TrashNewIcon className="w-3 h-3 shrink-0" />
          )}
          {hasDeletedDuration
            ? t('content-recording.editor-v2.restore-segment')
            : t('content-recording.editor-v2.remove-cut')}
        </button>
      )}
    </div>
  );
};
